import { useState, useEffect } from 'react';
import { Header } from './nav.jsx';
import { Footer, StickyBel, FloatingPhone } from './footer.jsx';
import { HomePage } from './home.jsx';
import { PlaagPage } from './plaag.jsx';
import { StadPage } from './stad.jsx';
import { StadPlaagPage } from './stad-plaag.jsx';
import { SpoedPage, BlogPage, ReviewsPage, ContactPage, OffertePage, DienstenPage, WerkgebiedPage, NotFoundPage, PrivacyPage, CookiesPage, VoorwaardenPage, OverOnsPage, HorecaPage } from './pages.jsx';
import { DiagnosticPage } from './diagnostic-page.jsx';
import { CookieConsentBanner } from './cookie-consent.jsx';
import { initAnalytics, trackPageView, trackEvent } from './analytics.js';
import { captureLeadSource, getLeadSourceMeta } from './lead-source.js';
import { PESTS, STEDEN } from './data.js';
import { navigate } from './url.js';

// Route patterns (history-API routing — real URLs, pre-renderable):
//   /                                home
//   /spoedservice                    24/7 emergency landing
//   /diensten                        services overview
//   /werkgebied                      service area overview
//   /blog                            kennisbank
//   /reviews                         all reviews
//   /contact, /offerte               forms
//   /{plaag}bestrijding              service detail (e.g. /wespenbestrijding)
//   /werkgebied/{stad}               city overview (e.g. /werkgebied/den-haag)
//   /{stad}/{plaag}bestrijding       combo (e.g. /den-haag/wespenbestrijding)

const FIXED_PAGES = {
  spoedservice: "spoed",
  diensten: "diensten",
  werkgebied: "werkgebiedOverzicht",
  blog: "blog",
  reviews: "reviews",
  contact: "contact",
  offerte: "offerte",
  privacy: "privacy",
  cookies: "cookies",
  "algemene-voorwaarden": "voorwaarden",
  "over-ons": "over",
  horeca: "horeca",
  diagnostic: "diagnostic",
};

function parseRoute() {
  const path = window.location.pathname || "/";
  if (path === "/" || path === "") return { type: "home" };
  const parts = path.split("/").filter(Boolean);

  // /spoedservice, /blog, /reviews, /contact, /offerte
  if (parts.length === 1) {
    if (FIXED_PAGES[parts[0]]) return { type: FIXED_PAGES[parts[0]] };
    // /{plaag}bestrijding
    if (parts[0].endsWith("bestrijding")) {
      const plaag = PESTS.find(p => parts[0] === p.slug + "bestrijding");
      if (plaag) return { type: "plaag", plaag };
    }
    return { type: "notfound", path };
  }

  // /werkgebied/{stad}  or  /{stad}/{plaag}bestrijding
  if (parts.length === 2) {
    if (parts[0] === "werkgebied") {
      const stad = STEDEN.find(s => s.slug === parts[1]);
      if (stad) return { type: "stad", stad };
      return { type: "notfound", path };
    }
    const stad = STEDEN.find(s => s.slug === parts[0]);
    if (stad && parts[1].endsWith("bestrijding")) {
      const plaag = PESTS.find(p => parts[1] === p.slug + "bestrijding");
      if (plaag) return { type: "stadplaag", stad, plaag };
    }
    return { type: "notfound", path };
  }

  return { type: "notfound", path };
}

function useRoute() {
  const [route, setRoute] = useState(parseRoute);
  useEffect(() => {
    const onPop = () => {
      setRoute(parseRoute());
      window.scrollTo({ top: 0, behavior: "instant" });
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
  return route;
}

// Intercept clicks on internal <a href="/..."> links so they go through the
// History API instead of triggering a full page reload. External links,
// new-tab clicks (cmd/ctrl/shift, target=_blank), in-page anchors (#foo),
// mailto:, tel:, and protocol-relative URLs all pass through untouched.
function useInternalLinkInterceptor() {
  useEffect(() => {
    const onClick = (e) => {
      if (e.defaultPrevented || e.button !== 0) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = e.target.closest && e.target.closest('a');
      if (!a) return;
      const href = a.getAttribute('href');
      if (!href || !href.startsWith('/')) return;
      if (a.target && a.target !== '_self') return;
      e.preventDefault();
      navigate(href);
    };
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, []);
}

export default function App() {
  const route = useRoute();
  useInternalLinkInterceptor();

  // Analytics initialiseren (idempotent, doet alleen iets als consent + GA_ID gegeven zijn)
  // + first-touch lead-herkomst opvangen (functioneel, geen tracking-cookie).
  useEffect(() => { initAnalytics(); captureLeadSource(); }, []);
  // Pageview-tracking bij elke route-change (SPA)
  useEffect(() => { trackPageView(); }, [route]);

  // Bel-knop-tracking: globale listener vangt élke tel:-link op de site af
  // (nav, hero, footer, sticky belbalk, stadspagina's) en stuurt een GA4
  // 'phone_call_click'-event met bron + pagina. Markeer ook dit event in
  // GA4 als conversie om telefoon-leads per kanaal te tellen.
  useEffect(() => {
    const onClick = (e) => {
      const a = e.target.closest && e.target.closest('a[href^="tel:"]');
      if (!a) return;
      try {
        trackEvent('phone_call_click', {
          ...getLeadSourceMeta(),
          phone: a.getAttribute('href').replace('tel:', ''),
        });
      } catch { /* analytics-fout mag een telefoon-klik nooit blokkeren */ }
    };
    document.addEventListener('click', onClick, { capture: true });
    return () => document.removeEventListener('click', onClick, { capture: true });
  }, []);

  let page;
  switch (route.type) {
    case "home":      page = <HomePage />; break;
    case "plaag":     page = <PlaagPage plaag={route.plaag} />; break;
    case "stad":      page = <StadPage stad={route.stad} />; break;
    case "stadplaag": page = <StadPlaagPage stad={route.stad} plaag={route.plaag} />; break;
    case "spoed":     page = <SpoedPage />; break;
    case "diensten":  page = <DienstenPage />; break;
    case "werkgebiedOverzicht": page = <WerkgebiedPage />; break;
    case "blog":      page = <BlogPage />; break;
    case "reviews":   page = <ReviewsPage />; break;
    case "contact":   page = <ContactPage />; break;
    case "offerte":   page = <OffertePage />; break;
    case "privacy":   page = <PrivacyPage />; break;
    case "cookies":   page = <CookiesPage />; break;
    case "voorwaarden": page = <VoorwaardenPage />; break;
    case "over":      page = <OverOnsPage />; break;
    case "horeca":    page = <HorecaPage />; break;
    case "diagnostic": page = <DiagnosticPage />; break;
    default:          page = <NotFoundPage path={route.path} />;
  }

  return (
    <>
      <a href="#main-content" className="skip-to-content">Sla menu over, ga naar inhoud</a>
      <Header routeType={route.type} />
      <main id="main-content" key={JSON.stringify(route)}>{page}</main>
      <Footer />
      <StickyBel />
      <FloatingPhone />
      <CookieConsentBanner />
    </>
  );
}

